import React from "react";
import {
  BadgeCheck,
  Check,
  CheckCircle2,
  Clock,
  Eye,
  Fingerprint,
  Loader2,
  QrCode,
  ReceiptText,
  Send,
  User,
  Wallet,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import pixService from "@/services/pixService";

/** Selo de status (card, detalhes e QR Code) */
export function PixStatusBadge({ paid, className }) {
  const Icon = paid ? BadgeCheck : Clock;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
        paid ? "bg-brand-pistachio/30 text-brand-dark" : "bg-terra-soft text-terra",
        className
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {paid ? "Pago" : "Pendente"}
    </span>
  );
}

/** "#298938" ou "Avulso" quando o PIX não tem pedido vinculado */
export const getPixTitle = (pix) =>
  pix?.orderCode ? `#${pix.orderCode}` : "Avulso";

const isPixMethod = (method) =>
  !method || String(method).trim().toLowerCase() === "pix";

/**
 * Card de uma cobrança gerada (docs/tela/code.html): pedido, status e
 * valor; cliente e forma; txid e data; ações conforme o status.
 *
 * Pendente: Ver QR Code + Marcar Pago. Pago: Ver Recibo + WhatsApp.
 * Clicar no corpo do card abre os detalhes completos.
 */
export default function PixPaymentCard({
  pix,
  loadingQr = false,
  onOpenDetails,
  onViewQRCode,
  onMarkReceived,
  onWhatsApp,
}) {
  const paid = pixService.isPaid(pix);
  const method = pix.pay_method || "PIX";
  const whatsApp = pixService.getWhatsAppNumber(pix.contact);
  const created = pixService.formatDate(pix.createdAt);
  const MethodIcon = paid ? CheckCircle2 : isPixMethod(method) ? QrCode : Wallet;

  return (
    <article className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-soft transition-shadow hover:shadow-ambient">
      <button
        type="button"
        onClick={() => onOpenDetails(pix)}
        aria-label={`Ver detalhes da cobrança ${getPixTitle(pix)}`}
        className="-m-1 flex flex-col gap-3 rounded-lg p-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {/* Pedido, status e valor */}
        <span className="flex w-full items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2">
            <span
              className={cn(
                "truncate rounded-full bg-surface-container px-2.5 py-1 font-display font-bold tracking-tight",
                pix.orderCode ? "text-base text-primary md:text-lg" : "text-sm text-muted-foreground"
              )}
            >
              {getPixTitle(pix)}
            </span>
            <PixStatusBadge paid={paid} />
          </span>
          <span className="whitespace-nowrap font-display text-base font-bold tracking-tight text-primary">
            {formatCurrency(pixService.getAmount(pix))}
          </span>
        </span>

        {/* Cliente e forma de pagamento */}
        <span className="flex w-full flex-col gap-1.5">
          <span className="flex items-center justify-between gap-3 text-xs">
            <span className="flex min-w-0 items-center gap-2">
              <User className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <span className="truncate text-[13px] font-semibold text-foreground">
                {pix.name || "Cliente não informado"}
              </span>
              {pix.contact && (
                <span className="hidden truncate text-muted-foreground min-[380px]:inline">
                  ({pixService.formatPhone(pix.contact)})
                </span>
              )}
            </span>
            <span className="inline-flex flex-shrink-0 items-center gap-1 text-[11px] font-semibold text-muted-foreground">
              <MethodIcon
                className={cn("h-4 w-4", paid ? "text-primary" : "text-secondary-foreground")}
              />
              {paid ? `${isPixMethod(method) ? "PIX" : method} recebido` : method}
            </span>
          </span>
          <span className="flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
            <span className="flex min-w-0 items-center gap-1">
              <Fingerprint className="h-4 w-4 flex-shrink-0" />
              <span className="truncate font-mono">{pix.txid || "-"}</span>
            </span>
            <span className="flex-shrink-0">{created}</span>
          </span>
        </span>
      </button>

      {/* Ações conforme o status */}
      <div className="grid grid-cols-2 gap-2 pt-1">
        {paid ? (
          <>
            <button
              type="button"
              onClick={() => onOpenDetails(pix)}
              className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-surface-container px-3 text-[13px] font-semibold text-foreground transition-colors hover:bg-surface-high active:scale-[0.98]"
            >
              <ReceiptText className="h-4 w-4" />
              Ver Recibo
            </button>
            <button
              type="button"
              onClick={() => onWhatsApp(pix)}
              disabled={!whatsApp}
              title={whatsApp ? "Enviar comprovante pelo WhatsApp" : "Contato sem telefone com DDD"}
              className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-brand-pistachio/25 px-3 text-[13px] font-semibold text-brand-dark transition-colors hover:bg-brand-pistachio/40 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              WhatsApp
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onViewQRCode(pix)}
              disabled={loadingQr || !pix.txid}
              className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-surface-container px-3 text-[13px] font-semibold text-primary transition-colors hover:bg-surface-high active:scale-[0.98] disabled:opacity-60"
            >
              {loadingQr ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              Ver QR Code
            </button>
            <button
              type="button"
              onClick={() => onMarkReceived(pix)}
              disabled={!pix.txid}
              className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-3 text-[13px] font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-brand-dark active:scale-[0.98] disabled:opacity-60"
            >
              <Check className="h-4 w-4" />
              Marcar Pago
            </button>
          </>
        )}
      </div>
    </article>
  );
}
